import { useCallback, useEffect, useState } from 'react';
import { CurrencyDollar, Lightning, Package, Tag, Warning } from 'shared/icons';
import api from '../../services/api';
import { CountLabel, FormatInt, FormatMoney } from '../../utils/format';
import Button from '../ui/Button';
import StatCard from '../ui/StatCard';
import { DASH, FormatIntOrDash, FormatPercentOrDash, IsNum } from './campaignCardShared';

/**
 * RESUMO DA VISÃO GERAL — quatro números do estado de HOJE dos anúncios ativos.
 *
 * Cada card responde uma pergunta do dono e LEVA à tela que detalha o número
 * (`onNavigate`): o rebate abre o Monitor já filtrado "Com rebate", as
 * campanhas abrem a lista das em andamento, a fila abre o Aplique agora. Por
 * isso os predicados do backend (`/campaigns/overview/summary`) espelham os
 * das telas de destino — o número do card é o que se vê ao clicar nele.
 *
 * Autossuficiente como o `CampaignApplyNow`: busca o próprio dado, tem
 * carregando/erro próprios, e uma falha AQUI não derruba o bloco antigo de
 * estatísticas logo abaixo (que continua vindo do `/overview/stats`) — nem a
 * dele derruba este. `refreshKey` é o Atualizar do header: mudou, busca de
 * novo; a montagem sozinha já busca uma vez.
 *
 * Nenhuma conta aqui (CLAUDE.md §4): somas, médias e contagens chegam prontas.
 * Dado ausente é '—', nunca 0 — um "0 anúncios com rebate" inventado por falta
 * de coluna diria que o ML não paga rebate nenhum.
 */

/** Junta só as partes que existem; nenhuma → sem linha de contexto. */
const joinParts = (parts) => parts.filter(Boolean).join(' · ') || null;

/** Card de rebate: soma de 1 unidade, média por anúncio e % do preço cheio. */
function rebateCard(rebate) {
    // Sem as colunas de percentual em campaign_items o backend não distingue
    // "sem rebate" de "sem dado": o card diz isso em vez de mostrar R$ 0,00.
    if (!rebate || rebate.available === false) {
        return { value: DASH, sub: 'indisponível' };
    }
    return {
        value: FormatMoney(rebate.amount_sum_per_unit),
        sub: joinParts([
            IsNum(rebate.amount_avg_per_unit) ? `média ${FormatMoney(rebate.amount_avg_per_unit)} por anúncio` : null,
            IsNum(rebate.percent_weighted) ? `${FormatPercentOrDash(rebate.percent_weighted)} do preço cheio` : null,
            IsNum(rebate.listings_with_rebate) ? CountLabel(rebate.listings_with_rebate, 'anúncio') : null,
        ]),
    };
}

/** "253 com campanha ativa · 26 sem" — só quando os dois lados vieram. */
function listingsSub(listings) {
    if (!IsNum(listings?.with_current_campaign) || !IsNum(listings?.without_current_campaign)) {
        return null;
    }
    return `${FormatInt(listings.with_current_campaign)} com campanha ativa · ${FormatInt(
        listings.without_current_campaign,
    )} sem`;
}

/**
 * "em 3 campanhas · 3 dos dois lados · 1 só líquido". Partes zeradas saem:
 * "0 só preço" é ruído numa linha que existe para dizer a MISTURA da fila.
 * Os rótulos das vantagens são os mesmos do cabeçalho do Aplique agora.
 */
function applyNowSub(applyNow) {
    // Zero é resposta, não ausência: diz que a fila está vazia de verdade.
    if (IsNum(applyNow?.listings) && Number(applyNow.listings) === 0) {
        return 'nenhuma troca aplicável hoje';
    }
    const byAdvantage = applyNow?.by_advantage || {};
    const part = (value, text) => (IsNum(value) && Number(value) > 0 ? text : null);
    return joinParts([
        part(applyNow?.target_campaigns, `em ${CountLabel(applyNow?.target_campaigns, 'campanha')}`),
        part(byAdvantage.both, `${FormatInt(byAdvantage.both)} dos dois lados`),
        part(byAdvantage.seller, `${FormatInt(byAdvantage.seller)} só líquido`),
        part(byAdvantage.consumer, `${FormatInt(byAdvantage.consumer)} só preço`),
    ]);
}

const CampaignOverviewSummary = ({ onNavigate = null, refreshKey = 0 }) => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // "Tentar novamente" só incrementa isto: a busca passa SEMPRE pelo efeito
    // abaixo, cuja limpeza invalida a resposta anterior. Chamar `load()` direto
    // do botão deixava a resposta do retry sem guarda — se o Atualizar do
    // header viesse logo depois, a resposta velha chegava por último e
    // sobrescrevia a nova (e um desmonte no meio fazia setState órfão).
    const [attempt, setAttempt] = useState(0);

    const load = useCallback(async (isAlive) => {
        setLoading(true);
        setError(null);
        try {
            const payload = await api.getCampaignOverviewSummary();
            if (isAlive()) {
                setSummary(payload || null);
            }
        } catch (err) {
            if (isAlive()) {
                setError(err?.message || 'Não foi possível carregar o resumo.');
                setSummary(null);
            }
        } finally {
            if (isAlive()) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        let alive = true;
        load(() => alive);
        return () => {
            alive = false;
        };
    }, [load, refreshKey, attempt]);

    // Card sem destino (quem monta o resumo não passou `onNavigate`) fica
    // estático em vez de virar um botão que não leva a lugar nenhum.
    const goTo = (target) => (typeof onNavigate === 'function' ? () => onNavigate(target) : null);

    if (error) {
        return (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-danger-200 bg-danger-soft p-4 text-sm text-danger-text dark:border-danger-900 dark:bg-danger-900/20 dark:text-danger-300">
                <Warning size={18} className="flex-none" />
                <span className="flex-1">Resumo de hoje indisponível: {error}</span>
                <Button onClick={() => setAttempt((n) => n + 1)} size="sm" variant="secondary">
                    Tentar novamente
                </Button>
            </div>
        );
    }

    // Sem a tabela de anúncios o backend responde `available: false`: não há
    // universo para contar, e quatro cards de '—' não explicariam o porquê.
    if (!loading && summary?.available === false) {
        return (
            <p className="rounded-xl border border-mono-200 bg-white p-4 text-sm text-mono-600 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400">
                Resumo de hoje indisponível: a base de anúncios ainda não foi sincronizada.
            </p>
        );
    }

    // Carregando: '—' e texto — nunca 0 (seria dado falso por meio segundo) e
    // nunca Skeleton, que é <div> e não pode morar dentro do <p> do card. Vale
    // para os QUATRO cards, inclusive no Atualizar do header: `summary` não é
    // limpo ao recarregar, então sem esta guarda três cards mostrariam o número
    // anterior com "carregando…" embaixo enquanto o rebate já estaria em '—'.
    const pending = loading ? 'carregando…' : null;
    const shown = (value) => (loading ? DASH : value);
    const rebate = rebateCard(summary?.rebate);

    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatCard
                label="Rebate do ML por unidade"
                value={shown(rebate.value)}
                sub={pending || rebate.sub}
                icon={CurrencyDollar}
                title="Soma do rebate de 1 unidade da campanha ativa de cada anúncio ativo. Não é valor recebido: o que o ML paga de fato aparece por venda em Pagamentos."
                onClick={goTo({ tab: 'items', monitorFilters: { rebate: 'with' } })}
            />
            <StatCard
                label="Anúncios ativos"
                value={shown(FormatIntOrDash(summary?.listings?.active))}
                sub={pending || listingsSub(summary?.listings)}
                icon={Package}
                title="Anúncios com status ativo no Mercado Livre. 'Com campanha ativa' = o anúncio está hoje numa campanha."
                // `status: 'active'` é o padrão do Monitor, mas vai explícito: o
                // card conta anúncios ATIVOS e o destino tem de mostrar os mesmos
                // — e, sendo um preset, tira da URL um deep-link de notificação
                // que abriria o universo `all` (ver goToTab em Campaigns.jsx).
                onClick={goTo({ tab: 'items', monitorFilters: { status: 'active' } })}
            />
            <StatCard
                label="Campanhas hoje"
                value={shown(FormatIntOrDash(summary?.campaigns?.running))}
                sub={
                    pending ||
                    (IsNum(summary?.campaigns?.current_on_active_listings)
                        ? `${FormatInt(summary.campaigns.current_on_active_listings)} valendo nos seus anúncios`
                        : null)
                }
                icon={Tag}
                title="Campanhas em andamento hoje: iniciadas, dentro do período e com pelo menos uma participação viva. 'Valendo' = quantas delas são a campanha ativa de algum anúncio ativo."
                // `running`, não `started`: a lista filtra pelo `is_running` do
                // backend, a MESMA condição deste número. Por status ela traria
                // também as campanhas fantasmas que o card não conta.
                onClick={goTo({ tab: 'campaigns', campaignsStatus: 'running' })}
            />
            <StatCard
                label="Anúncios com campanha melhor para aplicar"
                value={shown(FormatIntOrDash(summary?.apply_now?.listings))}
                sub={pending || applyNowSub(summary?.apply_now)}
                tone="success"
                icon={Lightning}
                // "Em andamento", como "Campanhas hoje" ao lado: a régua do
                // Aplique agora (backend) exige status `started` E estar dentro
                // do período — uma started que já venceu e o sync ainda não
                // encerrou não é alvo (decisão do dono, 30/09). O que segue fora
                // da régua é o prazo de adesão (deadline), de propósito.
                title="Anúncios com uma candidatura em campanha em andamento no Mercado Livre (já começou e ainda não terminou) que melhora o seu líquido ou o preço ao cliente sem piorar o outro lado — a mesma fila da aba Aplique agora."
                onClick={goTo({ tab: 'apply-now' })}
            />
        </div>
    );
};

export default CampaignOverviewSummary;
